if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (this.hasAttribute('data-gf-product-gallery')) this.prepareProductGallery();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        this.updateGalleryControls();
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      prepareProductGallery() {
        this.classList.add('gf-product-gallery');
        const description = this.closest('product-info')?.querySelector('.product__description');
        const list = this.elements.viewer.querySelector('.product__media-list');
        const thumbs = this.elements.thumbnails?.querySelector('.thumbnail-list');
        const videoKey = (src) => {
          try { const path = new URL(src, location.href).pathname; return path.match(/\/vp\/([^/]+)/)?.[1] || path; }
          catch { return src; }
        };
        const sources = (video) => [video.getAttribute('src'), ...Array.from(video.querySelectorAll('source')).map(s => s.getAttribute('src'))].filter(Boolean).map(videoKey);
        if (description && list && thumbs) {
          const heading = Array.from(description.querySelectorAll('h2,h3,h4')).find(h => /^videos?$/i.test(h.textContent.trim()));
          Array.from(description.querySelectorAll('video')).forEach((video, index) => {
            const keys = sources(video);
            const matching = Array.from(list.children).find(item => {
              const native = item.querySelector('template')?.content.querySelector('video') || item.querySelector('video');
              return native && sources(native).some(key => keys.includes(key));
            });
            const labelNode = video.previousElementSibling;
            const hasLabel = heading && (heading.compareDocumentPosition(video) & Node.DOCUMENT_POSITION_FOLLOWING) && labelNode?.tagName === 'P' && labelNode.children.length === 0 && labelNode.textContent.trim().length < 100;
            const label = hasLabel ? labelNode.textContent.trim() : `Product video ${index + 1}`;
            if (matching) {
              const thumb = Array.from(thumbs.children).find(item => item.dataset.target === matching.dataset.mediaId);
              thumb?.querySelector('button')?.setAttribute('aria-label', `Play video: ${label}`);
              video.remove();
            } else {
              const id = `${this.id}-description-video-${index + 1}`;
              const item = document.createElement('li');
              item.id = `Slide-${id}`;
              item.className = 'product__media-item grid__item slider__slide product__media-item--full';
              item.dataset.mediaId = id;
              const frame = document.createElement('div');
              frame.className = 'product-media-container media-type-video media-fit-contain global-media-settings gf-description-video';
              video.removeAttribute('style'); video.removeAttribute('autoplay');
              video.controls = true; video.playsInline = true; video.preload = 'none';
              video.setAttribute('aria-label', label);
              frame.append(video); item.append(frame); list.append(item);
              const thumb = document.createElement('li');
              thumb.id = `Slide-Thumbnails-${id}`;
              thumb.className = 'thumbnail-list__item slider__slide';
              thumb.dataset.target = id; thumb.dataset.mediaPosition = list.children.length;
              const button = document.createElement('button');
              button.type = 'button'; button.className = 'thumbnail global-media-settings gf-video-thumbnail';
              button.setAttribute('aria-label', `Play video: ${label}`);
              button.setAttribute('aria-controls', this.elements.viewer.id);
              const icon = document.createElement('span'); icon.textContent = '▶'; icon.setAttribute('aria-hidden', 'true');
              const text = document.createElement('span'); text.textContent = 'Video';
              button.append(icon, text); thumb.append(button); thumbs.append(thumb);
            }
            if (hasLabel) labelNode.remove();
          });
          if (heading && !description.querySelector('video') && !heading.nextElementSibling) heading.remove();
          this.elements.viewer.resetPages?.();
          this.elements.thumbnails.resetPages?.();
        }
        this.querySelectorAll('[data-gallery-step]').forEach(button => button.addEventListener('click', () => {
          const items = Array.from(list.children).filter(item => !item.hidden);
          const current = Math.max(0, items.findIndex(item => item.classList.contains('is-active')));
          const next = items[current + Number(button.dataset.galleryStep)];
          if (next) this.setActiveMedia(next.dataset.mediaId, false);
        }));
        this.sizeGallery = () => {
          const header = document.querySelector('.section-header');
          const padding = parseFloat(getComputedStyle(this.closest('product-info')).paddingTop) || 0;
          const top = (header?.getBoundingClientRect().height || 100) + padding;
          this.style.setProperty('--gf-gallery-top', `${Math.ceil(top)}px`);
        };
        this.sizeGallery();
        this.galleryResize = new ResizeObserver(this.sizeGallery);
        const header = document.querySelector('.section-header');
        if (header) this.galleryResize.observe(header);
        this.galleryResize.observe(this.closest('product-info'));
      }

      disconnectedCallback() { this.galleryResize?.disconnect(); }

      updateGalleryControls() {
        const controls = this.querySelector('.gf-gallery-navigation');
        if (!controls) return;
        const items = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]')).filter(item => item.classList.contains('product__media-item') && !item.hidden);
        const current = Math.max(0, items.findIndex(item => item.classList.contains('is-active')));
        controls.hidden = items.length < 2;
        controls.querySelector('[data-gallery-step="-1"]').disabled = current === 0;
        controls.querySelector('[data-gallery-step="1"]').disabled = current === items.length - 1;
        controls.querySelector('.gf-gallery-position').textContent = `${current + 1} of ${items.length}`;
      }

      onSlideChanged(event) {
        if (!event.detail.currentElement) return;
        if (this.classList.contains('gf-product-gallery') && !this.mql.matches) {
          const changed = !event.detail.currentElement.classList.contains('is-active');
          this.elements.viewer.querySelectorAll('.product__media-item').forEach(item => item.classList.toggle('is-active', item === event.detail.currentElement));
          if (changed) window.pauseAllMedia();
          this.updateGalleryControls();
        }
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');
        this.updateGalleryControls();

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}

